import type { InfiniteData, Query, QueryClient, QueryFilters } from '@tanstack/react-query';

import type { Contact } from '../../domain/contact';

import { CONTACTS_KEY, isContactListQueryKey } from './companyContactList/contactListQueryKey';
import { isContactsCountByCountriesQueryKey } from './companyContactList/contactsCountByCountriesQueryKey';
import {
  CONTACTS_BY_LINKEDIN_IDS_KEY,
  isLinkedinContactsListQueryKey,
} from './linkedinContactsList/linkedinContactsListQueryKey';

export const CONTACT_KEY = 'contact';

export type ContactRepository = {
  getContactById(contactId: string): Contact | undefined;
  saveContact(contact: Contact): void;
  invalidateAllContactListQueries(refetch?: boolean): void;
};

export const makeContactRepository = (queryClient: QueryClient): ContactRepository => {
  const saveSingleContact = (contact: Contact) => {
    queryClient.setQueriesData<Contact>(
      {
        queryKey: [CONTACT_KEY],
        predicate: (query) => (query.state.data as Contact)?.id === contact.id,
      },
      () => contact
    );
  };

  const saveContactInList = (contact: Contact) => {
    queryClient.setQueriesData<InfiniteData<Contact[]>>(
      {
        queryKey: [CONTACTS_KEY],
        predicate: (query) => {
          return (query.state.data as InfiniteData<Contact[]>)?.pages
            .flat()
            .some((contactItem) => contactItem.id === contact.id);
        },
      },
      (queryData: InfiniteData<Contact[]> | undefined) => {
        const pages = queryData?.pages || [];
        const updateContact = (contactItem: Contact): Contact => {
          return contactItem.id === contact.id ? contact : contactItem;
        };
        const updatedPages = pages.map((page) => page.map(updateContact));

        return { pages: updatedPages, pageParams: queryData?.pageParams as unknown[] };
      }
    );
  };

  const saveContactInSearchList = (contact: Contact) => {
    const findQueryWithContactToUpdate = (query: Query<(Contact | null)[]>) => {
      const contacts = query.state.data ?? [];
      return contacts.some((contactItem) => contactItem?.id === contact.id);
    };

    const updateContactInList = (queryData: (Contact | null)[] | undefined): (Contact | null)[] => {
      const updateContacts = (c: Contact | null) => (c?.id === contact.id ? contact : c);
      return queryData?.map(updateContacts) ?? [];
    };

    queryClient.setQueriesData<(Contact | null)[]>(
      { queryKey: [CONTACTS_BY_LINKEDIN_IDS_KEY], predicate: findQueryWithContactToUpdate },
      updateContactInList
    );
  };

  const getContactByIdFromSingleContactQuery = (contactId: string) => {
    let contact: Contact | undefined;

    queryClient.getQueriesData<Contact>({
      queryKey: [CONTACT_KEY],
      exact: false,
      predicate: (query) => {
        if (query.state.data?.id === contactId) {
          contact = query.state.data;
          return true;
        }
        return false;
      },
    });

    return contact;
  };

  const getContactByIdFromListQuery = (contactId: string) => {
    let contact: Contact | undefined;

    queryClient.getQueriesData<InfiniteData<Contact[]>>({
      queryKey: [CONTACTS_KEY],
      exact: false,
      predicate: (query) => {
        const pages = (query.state.data as InfiniteData<Contact[]>)?.pages ?? [];
        for (const page of pages) {
          contact = page.find((c) => c.id === contactId);
          if (contact) {
            return true;
          }
        }

        return false;
      },
    });

    return contact;
  };

  const getContactByIdFromSearchListQuery = (contactId: string) => {
    let contact: Contact | undefined;

    const findContactFilter: QueryFilters = {
      exact: false,
      predicate: (query: Query) => {
        if (contact) {
          return true;
        }
        const contacts = (query.state.data as Contact[]) ?? [];
        contact = contacts.find((c) => c.id === contactId);
        return !!contact;
      },
    };

    queryClient.getQueryData<Contact>({ queryKey: [CONTACTS_BY_LINKEDIN_IDS_KEY], ...findContactFilter });

    return contact;
  };

  const invalidateAllContactListQueries = (refetch?: boolean) => {
    queryClient
      .invalidateQueries({
        predicate: ({ queryKey }) => {
          return [isContactListQueryKey, isLinkedinContactsListQueryKey, isContactsCountByCountriesQueryKey].some(
            (isListQueryFn) => isListQueryFn(queryKey)
          );
        },
        refetchType: refetch ? 'active' : 'none',
      })
      .catch(console.error);
  };

  return {
    saveContact(contact: Contact) {
      saveSingleContact(contact);
      saveContactInList(contact);
      saveContactInSearchList(contact);
      invalidateAllContactListQueries();
    },
    getContactById(contactId: string) {
      return (
        getContactByIdFromSingleContactQuery(contactId) ||
        getContactByIdFromListQuery(contactId) ||
        getContactByIdFromSearchListQuery(contactId)
      );
    },
    invalidateAllContactListQueries(refetch) {
      invalidateAllContactListQueries(refetch);
    },
  };
};
